import { useIntl } from 'react-intl'
import {
  ICustomerAddress,
  PaymentInstrument,
  PAYMENT_METHODS,
  SUPPORTED_PAYMENT_METHODS,
  useCountryData
} from 'msp-integrations'
import type { FieldsConfiguration } from 'msp-components'
import translations from '@/components/molecules/PaymentMethodDisplay/PaymentMethodDisplay.constants'

const getOrderSubscriptionFields = (
  addresses: ICustomerAddress[] = [],
  paymentInstruments: PaymentInstrument[] = [],
  onPaymentMethodChange: (evt: any) => void = () => {}
): FieldsConfiguration => {
  const { formatMessage } = useIntl()
  const i18n = useCountryData()

  const addressDisplay = (address: ICustomerAddress) => {
    return {
      id: address.addressId,
      value: `${address.firstName} ${address.lastName} - ${address.address1} ${
        address.address2 || ''
      } - ${address.city} ${address.postalCode} - ${address.countryCode}`,
      disabled: ''
    }
  }
  const addressOptions = addresses.map(addressDisplay)

  const billingAddressOptions = addresses
    .filter(
      (addr) =>
        !addr?.c_isPackstation && addr?.countryCode === i18n.currentCountry.code
    )
    .map(addressDisplay)

  const paymentMethodOptions = paymentInstruments
    .filter((paymentInstrument) =>
      SUPPORTED_PAYMENT_METHODS.includes(paymentInstrument.paymentMethodId)
    )
    .map((paymentInstrument) => {
      let value: string
      let address: ICustomerAddress
      switch (paymentInstrument.paymentMethodId) {
        case PAYMENT_METHODS.unzer_cards:
          value = `${formatMessage(
            translations[paymentInstrument.paymentMethodId],
            {
              cardType: paymentInstrument.paymentCard.cardType
            }
          )} - ${paymentInstrument.paymentCard.maskedNumber}`
          break
        case PAYMENT_METHODS.unzer_direct_debit_secured:
          address = JSON.parse(paymentInstrument.c_billingAddress)
          value = `${formatMessage(
            translations[paymentInstrument.paymentMethodId]
          )} (${paymentInstrument.c_maskedNumber}) - (${address.firstName} ${
            address.lastName
          } - ${address.address1} ${address.address2 || ''} - ${address.city} ${
            address.postalCode
          } - ${address.countryCode})`
          break
        case PAYMENT_METHODS.unzer_invoice:
          address = JSON.parse(paymentInstrument.c_billingAddress)
          value = `${formatMessage(
            translations[paymentInstrument.paymentMethodId]
          )} (${address.firstName} ${address.lastName} - ${address.address1} ${
            address.address2 || ''
          } - ${address.city} ${address.postalCode} - ${address.countryCode})`
          break
        default:
          value = formatMessage(translations[paymentInstrument.paymentMethodId])
          break
      }

      return {
        id: paymentInstrument.paymentInstrumentId,
        value,
        disabled: ''
      }
    })

  const validateStartDate = (date: string) => {
    var validDate = new Date()
    validDate.setDate(validDate.getDate() + 2)
    var startDate = new Date(date)

    return validDate < startDate
  }

  return {
    shippingAddress: {
      id: 'shippingAddress',
      type: 'select',
      name: 'shippingAddress',
      defaultValue: addressOptions[0].id,
      label: formatMessage({
        defaultMessage: 'Shipping Address',
        id: 'msp.subscription_form.shipping_address.label'
      }),
      options: addressOptions
    },
    billingAddress: {
      id: 'billingAddress',
      type: 'select',
      name: 'billingAddress',
      defaultValue: billingAddressOptions[0].id,
      label: formatMessage({
        defaultMessage: 'Billing Address',
        id: 'msp.subscription_form.billing_address.label'
      }),
      options: billingAddressOptions
    },
    startDate: {
      id: 'startDate',
      type: 'date',
      name: 'startDate',
      label: formatMessage({
        defaultMessage: 'Start Date',
        id: 'msp.subscription_form.start_date.label'
      }),
      rules: {
        required: formatMessage({
          defaultMessage: 'Please select a start date.',
          id: 'msp.subscription_form.start_date.error.required'
        }),
        validate: (value) =>
          validateStartDate(value) ||
          formatMessage({
            defaultMessage:
              'Starting date must be at least 3 days away from today.',
            id: 'msp.subscription_form.start_date.error.required'
          })
      }
    },
    interval: {
      id: 'interval',
      type: 'number',
      name: 'interval',
      defaultValues: 1,
      additionalProps: {
        min: 1,
        max: 24,
        step: 1
      },
      label: formatMessage({
        defaultMessage: 'Interval in Weeks',
        id: 'msp.subscription_form.interval.label'
      }),
      rules: {
        required: formatMessage({
          defaultMessage: 'Please fill out an interval.',
          id: 'msp.subscription_form.interval.error.required'
        })
      }
    },
    paymentMethod: {
      id: 'paymentMethod',
      type: 'select',
      name: 'paymentMethod',
      defaultValues: paymentMethodOptions[0]?.id,
      label: formatMessage({
        defaultMessage: 'Payment',
        id: 'msp.subscription_form.payment_method.label'
      }),
      rules: {
        required: formatMessage({
          defaultMessage: 'Please select a payment.',
          id: 'msp.subscription_form.payment_method.error.required'
        })
      },
      onChange: (evt: any) => {
        onPaymentMethodChange(evt)
      },
      options: paymentMethodOptions
    }
  }
}

export default getOrderSubscriptionFields
